import produce from 'immer';

import {LiveCellValue} from 'notebook/structured-tables/LiveCellValues';
import {Cell, TypedValue} from 'notebook/tables/TableConstants';

/**
 * Used to handle certain special cases like if the text value should be not '' or if we need to
 *  return an empty cell in response to a null liveValue. The special casing here was implemented to preserve
 *  behavior after a bug fix.
 *
 * @param setRawTextValue - used for computing the text field from the live value. Right now, only used
 *  in a special case in RegistrationTableRowUpdater. Usually defaults to an empty string.
 * @param setCellBlankIfLiveValueNull - some rowupdaters have different behaviors if they encounter a null
 *  live value. In these cases, we lets the calling class decide on the behavior.
 */
interface UpdateOptions {
  setRawTextValue?: (typeValue: TypedValue) => string;
  setCellBlankIfLiveValueNull?: boolean;
}

/**
 * A utility function to handle updating cells in row updater classes without
 * blowing away any special properties or formatting like precision and underlining
 * that may be associated with the cell.
 *
 * @param cell - the table cell being updated
 * @param liveValue - a LiveCellValue to update the specified cell with. If NO_VALUE or null,
 *  we make no changes to the initial cell.
 * @param updateOptions - See the updateCellWithLiveValues#UpdateOptions interface for a description of this
 *  argument. Used to tell the function how to behave in certain special circumstances.

 * @returns An updated copy of the input cell containing the new live value, with formatting
 *  properties intact.
 */
// Keep in sync with update_cell_with_live_value in benchling/notebook/structured_tables/resync/cell_update.py
export default function updateCellWithLiveValue(
  cell: Cell,
  liveValue: LiveCellValue,
  options?: UpdateOptions
): Cell {
  return produce(cell, (draft: Cell) => {
    // This conditional should always evaluate to true
    if (liveValue == null) {
      if (options && options.setCellBlankIfLiveValueNull) {
        draft.text = '';
        delete draft.typedValue;
      }
    } else if (liveValue !== 'NO_VALUE') {
      draft.text = options?.setRawTextValue?.(liveValue) ?? '';
      draft.typedValue = liveValue;
    }
  });
}
